'use client';

import { useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import type { Project, ProjectSortByField, SortOrder } from '@/shared/lib/types';
import { fetchProjects, createProject, deleteProject } from '@/features/projects/services/projectsService';
import { fetchUsers } from '@/features/users/services/usersService';
import { translate } from '@/shared/lib/locales';
import { useWebSocket } from '@/shared/hooks/useWebSocket';
import { useIndexingStore } from '@/store/indexingStore';

const DEFAULT_PAGE_SIZE = 10;
// Estado inicial da tabela: descendente por data de criação (pedido do
// utilizador) — distinto do estado "sem ordenação nesta coluna" (sortBy
// null), que só se atinge ciclando os cabeçalhos e reverte para a ordem
// atual da tabela (por ID do projeto, ver ProjectRepository.find_by_user_id).
const DEFAULT_SORT_BY: ProjectSortByField = 'created_at';
const DEFAULT_SORT_ORDER: SortOrder = 'desc';

export function useProjectsPage(sessionId: string) {
  const { data: authSession } = useSession();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<ProjectSortByField | null>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<SortOrder | null>(DEFAULT_SORT_ORDER);

  useEffect(() => {
    if (sessionId) loadProjects();
  }, [sessionId, pageNumber, pageSize, sortBy, sortOrder]);

  const handlePageSizeChange = (size: number) => {
    setPageNumber(1);
    setPageSize(size);
  };

  // Ciclo por coluna: ascendente -> descendente -> sem ordenação -> ascendente.
  // Clicar numa coluna diferente da ativa recomeça-a em ascendente (só uma
  // coluna pode estar ativa de cada vez).
  const handleSortChange = (column: ProjectSortByField) => {
    setPageNumber(1);
    if (sortBy !== column) {
      setSortBy(column);
      setSortOrder('asc');
      return;
    }
    if (sortOrder === 'asc') {
      setSortOrder('desc');
      return;
    }
    setSortBy(null);
    setSortOrder(null);
  };

  // Rede de segurança: qualquer projeto que arranque/termine a indexação
  // (com sucesso ou erro) atualiza a lista em tempo real, sem precisar de
  // refresh manual da página.
  useWebSocket(undefined, {
    onProjectStatusUpdated: () => loadProjects(),
    onIndexingComplete: () => loadProjects(),
    onIndexingError: () => loadProjects(),
    // O socket esteve em baixo: os estados que mudaram entretanto nunca
    // chegaram aqui, e a lista mostrava "A processar" em projetos já
    // terminados até alguém fazer refresh.
    onReconnected: () => loadProjects(),
  });

  useEffect(() => {
    const email = authSession?.user?.email;
    if (!email) return;
    fetchUsers({ email }).then((result) => {
      if (result.items.length > 0) setCurrentUserId(result.items[0].user_id);
    }).catch(() => {});
  }, [authSession?.user?.email]);

  const loadProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchProjects(sessionId, { pageNumber, pageSize, sortBy, sortOrder });
      setProjects(result.items);
      setTotal(result.total);
      // O estado de indexação que vem nesta listagem é a fonte de verdade da
      // barra de progresso: fecha as que já terminaram (mensagem de conclusão
      // perdida, socket em baixo) e repõe as que ainda decorrem depois de um
      // F5. Ver `syncProjectFromServer`.
      const syncProject = useIndexingStore.getState().syncProjectFromServer;
      result.items.forEach(syncProject);
    } catch (err) {
      setError(translate('hookMessages', 'loadProjectsWithReasonError', { message: (err as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async () => {
    const name = newProjectName.trim();
    if (!name) return;

    setIsCreating(true);
    setError(null);
    try {
      await createProject(name);
      await loadProjects();
      setNewProjectName('');
    } catch (err) {
      setError(translate('hookMessages', 'createProjectWithReasonError', { message: (err as Error).message }));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (projectId: string) => {
    setError(null);
    try {
      await deleteProject(projectId);
      await loadProjects();
    } catch (err) {
      setError(translate('hookMessages', 'removeProjectWithReasonError', { message: (err as Error).message }));
    }
  };

  return {
    projects,
    isLoading,
    newProjectName,
    setNewProjectName,
    isCreating,
    error,
    handleCreate,
    handleDelete,
    loadProjects,
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize: handlePageSizeChange,
    total,
    sortBy,
    sortOrder,
    onSortChange: handleSortChange,
  };
}
